import { batch, type Accessor, type Setter } from 'solid-js'
import type { SetStoreFunction } from 'solid-js/store'
import { IObjectImpl, ObjectImpl, ObjectType, type GeometrySeed } from '../interactions/ObjectImpl'
import type { SelectionContext } from '../selection'
import type { HistoryContext } from '../../project/history'
import type { ObjectContext } from '../../project/objects'
import { Mode } from '../../project/PosterMode'
import type { DragContext } from '../drag'
import type { ImageContext } from './images'
import type { UploadContext } from './upload'
import { setImageFieldCmd, setImageSrcCmd, setImageUploadCmd, setImageCropCmd } from '../../project/commands'
import { setImageSrcInStore } from 'Shared/posters/project/objects'
import type { RuntimeImage, DataRecord, Box } from '../../types'
import type { Canvas, Crop } from '../../project/posterSchema'
import { type ThemeRecord } from '../../project/themeVariables';
import { effectiveImageSrc } from './effectiveSrc';
import { useUrls } from 'Shared/UrlProvider';


export class ImageImpl extends ObjectImpl implements IObjectImpl
{
	readonly id: string;
	readonly isFullBleed: boolean;
	readonly movable: boolean;

	constructor(
		box:    Box,
		setBox: SetStoreFunction<Box>,
		seed:   GeometrySeed,
		readonly pan:     Accessor<{ x: number; y: number }>,
		readonly setPan:  Setter<{ x: number; y: number }>,
		readonly zoom:    Accessor<number>,
		readonly setZoom: (z: number) => void,
		readonly stretch: Accessor<number>,
		readonly setStretch: (r: number) => void,
		readonly imgEl:      Accessor<HTMLImageElement | null>,
		readonly setImgEl:   (el: HTMLImageElement | null) => void,
		readonly shiftDragging: Accessor<boolean>,
		readonly setShiftDragging: (v: boolean) => void,
		readonly isEditingFieldS: Accessor<boolean>,
		readonly setIsEditingFieldS: (v: boolean) => void,
		readonly config:  ImageContext,
		readonly object:  RuntimeImage,
		readonly canvas:  Canvas,
		readonly useUser: () => boolean,
		readonly record:  DataRecord,
		readonly theme:   ThemeRecord | undefined,
		readonly getFileInput: () => HTMLInputElement | undefined,
	)
	{
		super(box,setBox,seed);
		this.id = this.object.id;
//XXX do we need both of these?		
		this.isFullBleed = !this.object.position;
		this.movable = !!this.object.position;
	}

	type() { return 'image' as ObjectType; }

	isField() { return !!this.object.isField; }

	resizeHandles() { return this.isFullBleed ? 'none' : 'box'; }

	lockAspect() { return true; }

	// User mode: pull the image back inside the parent (nearest container / canvas) so an image placed
	// past the designed layout is nudged just inside its bounds. Full-bleed images intentionally fill
	// the canvas, and flex-managed children are positioned by their container — both keep their box.
	// Derived only — the designed position is never persisted.
	effectiveBox(userExists:boolean): Box
	{
		const base = super.effectiveBox(userExists);
		if (this.isFullBleed || this.layoutPos?.() || !userExists)
			return base;
		return this.clampBoxIntoParent(base, this.canvas.width, this.canvas.height)
	}

	// Live image source read by export/mode-switch and written by drag-drop, via the element's impl.
	getImageSrc(): string | null { return this.object.src ?? null }
	setImageSrc(newSrc: string | null, objCtx: ObjectContext): void { setImageSrcInStore(objCtx, this.object.id, newSrc) }

	// ── Inert host surface ─────────────────────────────────────────────────────────
	// An image has no fill/border query, no min-size fitting, and none of the text wiring; these
	// satisfy the abstract ObjectImpl surface with the same inert results the host already branches on.

	getHugFit() { return undefined; }
	fieldHeight() { return undefined; }

	// ── Component-facing surface ──────────────────────────────────────────────────

	setImageElement(el: HTMLImageElement | null): void { this.setImgEl(el) }

	// The live crop view (zoom/stretch/pan), read at serialize time to persist it on the object.
	getCrop(): Required<Crop> { return { zoom: this.zoom(), stretch: this.stretch(), pan: this.pan() } }

	// Apply a saved crop wholesale (used by the zoom/stretch/pan history commands). setZoom is the
	// centred wrapper that rescales the current pan, so set pan explicitly afterwards to land on
	// the exact saved offset rather than the rescaled one.
	applyCrop(crop: Required<Crop>): void
	{
		batch(() => {
			this.setZoom(crop.zoom)
			this.setStretch(crop.stretch)
			this.setPan(crop.pan)
		})
	}

	isEditingField(): boolean { return this.isEditingFieldS() }

	// Commit a field-name edit (design-mode rebind of the image source field).
	finishFieldEdit(value: string, objCtx: ObjectContext, histCtx: HistoryContext, selCtx: SelectionContext): void
	{
		const obj = this.object
		const before = obj.field ?? ''
		histCtx.execute(setImageFieldCmd(objCtx, obj.id, before, value))
		this.setIsEditingFieldS(false)
		selCtx.stopEdit()
	}

	async handleFileChange(e: Event, objCtx: ObjectContext, histCtx: HistoryContext, uploadCtx: UploadContext | undefined): Promise<void>
	{
		const input = e.target as HTMLInputElement
		const file  = input.files?.[0]
		if (!file) return
		input.value = ''

		const obj = this.object

		if (uploadCtx) {
			// Upload to the asset store and persist only the returned hash; inlining a
			// base64 data URL would bloat the saved design and blow the request body-size
			// limit when AutoSave persists it on the design page.
			const upload = await uploadCtx.uploadImage(file)
			if (upload != null)
				histCtx.execute(setImageUploadCmd(objCtx, obj.id, obj.image ?? null, upload))
		} else {
			// Fallback for non-server contexts (standalone/testing): keep the image in
			// browser memory as a data URL since there's no asset store to upload to.
			const before = obj.src ?? null
			const reader = new FileReader()
			reader.onload = ev => {
				const after = (ev.target as FileReader).result as string
				histCtx.execute(setImageSrcCmd(objCtx, obj.id, before, after))
			}
			reader.readAsDataURL(file)
		}
	}

	// ── Internal ──────────────────────────────────────────────────────────────────

	cursorOverride(selCtx: SelectionContext): string
	{
		if (this.isFullBleed)            return 'crosshair'
		if (this.shiftDragging())        return 'grabbing'
		if (selCtx.shiftHeld())          return 'grab'
		if (this.isSelected(selCtx))     return 'move'
		return 'pointer'
	}

	// Full-bleed images select but never drag; every other image (and text/container) is movable.
	isMovable(): boolean { return this.movable }

	// Same-element double-click detection is enabled for images (upload/rebind on the second click).
	handlesDoubleClick(): boolean { return true }

	// Shift-drag pans the image within its frame (crop) rather than moving the object.
	onShiftDrag(e: MouseEvent, dragCtx: DragContext, histCtx: HistoryContext): void { this.beginCrop(e, dragCtx, histCtx) }

	private isSelected(selCtx: SelectionContext): boolean { return selCtx.selectedIds().has(this.object.id) }

	// Shift-drag pans the image within its frame (crop) rather than moving the object.
	beginCrop(e: MouseEvent, dragCtx: DragContext, histCtx: HistoryContext): void
	{
		this.setShiftDragging(true)
		const before = this.getCrop()
		const start  = this.pan()
		dragCtx.beginDrag({ x: e.clientX, y: e.clientY },
			(dx, dy) => this.setPan({ x: start.x + dx, y: start.y + dy }),
			() => {
				// Commit the pan as one undoable step so AutoSave persists the new crop offset.
				const after = this.getCrop()
				if (after.pan.x !== before.pan.x || after.pan.y !== before.pan.y)
					histCtx.execute(setImageCropCmd(this, before, after))
			},
		)
	}

	// User-facing contexts open the image uploader. In design mode the source type decides:
	// "Field" rebinds the source field path; "Upload" opens the uploader.
	handleDoubleClick(e:MouseEvent, selCtx: SelectionContext, mode: Mode)
	{
		if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey)
			return;

		const obj = this.object
		if (mode=='read')
			return;
		if (mode=='user') { 
			this.getFileInput()?.click(); 
			return;
		}
		if (this.isField()) { 
			this.setIsEditingFieldS(true); 
			selCtx.startEdit(obj.id); 
			return;
		}
		this.getFileInput()?.click()
	}

	// Resolves the image's display URL from its upload/field/binding state. The impl memo in
	// ImageObject tracks P.record at construction, so a record change rebuilds the impl and re-runs
	// the effectiveSrc memo — this.record is always the current record, and a field image
	// repopulates on record change.
	effectiveSrc(): string | null
	{
		// Read useUrls() here, inside the memo body, rather than once at setup: PosterPage's
		// site key arrives asynchronously, so a snapshot would stay stale.
		return effectiveImageSrc(this.object, this.record, this.theme, useUrls(), this.config.proxyProductionAssets)
	}
}
